import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/utilityApiConfig';
import { isLocal } from '../config/env';
import { getSelectedUtility } from '../utils/utilitySelection';
import { getMdhdWorkflowConfig } from '../utils/mdhdWorkflowConfig';
import { getUtilityWorkflowUiState } from '../utils/utilityWorkflowUiState';
import LongRunningTaskIndicator from '../components/general/LongRunningTaskIndicator';

const UtilityMdhdAnalysisContext = createContext(null);

const MDHD_SIMULATION_START_YEAR = 2027;
const MDHD_SIMULATION_END_YEAR = 2050;
const RUN_STATE_STALE_MS = 30 * 60 * 1000;

function getMdhdSimulationEndYear(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return MDHD_SIMULATION_END_YEAR;
  return Math.max(
    MDHD_SIMULATION_START_YEAR,
    Math.min(MDHD_SIMULATION_END_YEAR, Math.round(numericValue))
  );
}

function getMdhdSimulationYears(endYear) {
  const resolvedEndYear = getMdhdSimulationEndYear(endYear);
  return Array.from(
    { length: resolvedEndYear - MDHD_SIMULATION_START_YEAR + 1 },
    (_, index) => MDHD_SIMULATION_START_YEAR + index
  );
}

function getRunStateStorageKey() {
  const token = localStorage.getItem('authToken') || 'anonymous';
  return `utility-workflow-run-state-${token.slice(-16)}`;
}

function loadRunStateForUser() {
  try {
    const raw = window.sessionStorage.getItem(getRunStateStorageKey());
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const startedAt = Number(parsed.startedAt || 0);
    if (startedAt > 0 && Date.now() - startedAt > RUN_STATE_STALE_MS) {
      window.sessionStorage.removeItem(getRunStateStorageKey());
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function saveRunStateForUser(runState) {
  try {
    if (!runState) {
      window.sessionStorage.removeItem(getRunStateStorageKey());
      return;
    }
    window.sessionStorage.setItem(getRunStateStorageKey(), JSON.stringify(runState));
  } catch {
    // Ignore storage failures.
  }
}

function interpolateForecastValue(points, targetYear) {
  const sorted = (points || [])
    .map((pt) => ({
      year: Number(pt?.year),
      vehicles: Number(pt?.vehicles),
    }))
    .filter((pt) => Number.isFinite(pt.year) && Number.isFinite(pt.vehicles))
    .sort((a, b) => a.year - b.year);

  if (!sorted.length) return null;
  const exact = sorted.find((pt) => pt.year === targetYear);
  if (exact) return exact.vehicles;

  const before = [...sorted].reverse().find((pt) => pt.year < targetYear);
  const after = sorted.find((pt) => pt.year > targetYear);
  if (before && after && after.year !== before.year) {
    const ratio = (targetYear - before.year) / (after.year - before.year);
    return before.vehicles + ratio * (after.vehicles - before.vehicles);
  }
  if (before) return before.vehicles;
  if (after) return after.vehicles;
  return null;
}

function normalizeManagedChargingMode(v2gMode) {
  if (v2gMode === 'active_managed') return 'active';
  if (v2gMode === 'passive_managed') return 'passive';
  return 'unmanaged';
}

function formatManagedChargingLabel(v2gMode) {
  if (v2gMode === 'active_managed') return 'Active Managed';
  if (v2gMode === 'passive_managed') return 'Passive Managed';
  return 'Unmanaged';
}

function buildMdhdCaseName(utilityName, hdScenario, v2gMode) {
  const utilityLabel = String(utilityName || 'Utility').trim() || 'Utility';
  const hdScenarioLabel = String(hdScenario || 'Class 7-8 HD').trim() || 'Class 7-8 HD';
  return `${utilityLabel} ${hdScenarioLabel} ${formatManagedChargingLabel(v2gMode)}`;
}

export function UtilityMdhdAnalysisProvider({ children }) {
  const navigate = useNavigate();
  const mountedRef = useRef(true);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisError, setAnalysisError] = useState('');
  const [analysisPhase, setAnalysisPhase] = useState(null);
  const [analysisStartedAt, setAnalysisStartedAt] = useState(null);
  const [analysisElapsedSeconds, setAnalysisElapsedSeconds] = useState(0);
  const [analysisAbortController, setAnalysisAbortController] = useState(null);
  const [analysisRequestId, setAnalysisRequestId] = useState(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const runState = loadRunStateForUser();
    if (!runState?.isRunning) return;
    setAnalysisLoading(true);
    setAnalysisPhase(runState.phase || 'Resuming in-progress analysis...');
    const startedAt = Number(runState.startedAt || Date.now());
    setAnalysisStartedAt(startedAt);
    setAnalysisElapsedSeconds(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
    setAnalysisRequestId(runState.requestId || null);
  }, []);

  useEffect(() => {
    if (!analysisLoading || !analysisStartedAt) {
      setAnalysisElapsedSeconds(0);
      return undefined;
    }
    const timerId = window.setInterval(() => {
      if (!mountedRef.current) return;
      setAnalysisElapsedSeconds(Math.max(0, Math.floor((Date.now() - analysisStartedAt) / 1000)));
    }, 1000);
    return () => window.clearInterval(timerId);
  }, [analysisLoading, analysisStartedAt]);

  const clearAnalysisError = useCallback(() => {
    setAnalysisError('');
  }, []);

  const startAnalysis = useCallback(async () => {
    if (analysisLoading) return;

    setAnalysisLoading(true);
    setAnalysisError('');
    setAnalysisPhase('Fetching MDHD forecast curve...');
    const startedAt = Date.now();
    setAnalysisStartedAt(startedAt);
    setAnalysisElapsedSeconds(0);
    const requestId = `mdhd-${startedAt}`;
    setAnalysisRequestId(requestId);
    saveRunStateForUser({
      isRunning: true,
      startedAt,
      requestId,
      phase: 'Fetching MDHD forecast curve...',
    });
    const controller = new AbortController();
    setAnalysisAbortController(controller);

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Missing auth token. Please log in again.');
      }

      const latestUtilitySelection = getSelectedUtility();
      const workflowUiState = getUtilityWorkflowUiState();
      const utilityName = workflowUiState?.mdhdAdoptionTrend?.selectedUtility || latestUtilitySelection?.utilityName;
      const simulationYears = getMdhdSimulationYears(
        workflowUiState?.mdhdAdoptionTrend?.simulationEndYear
      );
      if (!utilityName) {
        throw new Error('No utility is selected. Please select a utility first.');
      }

      const mdhdConfig = getMdhdWorkflowConfig();
      const chargingBehaviorConfig = mdhdConfig?.evChargingBehavior || mdhdConfig?.vehicleConfiguration || {};
      const curveResponse = await axios.get(API_ENDPOINTS.getMdhdForecastCurves, {
        headers: { Authorization: `Token ${token}` },
        params: { utility_name: utilityName },
        signal: controller.signal,
      });

      const curvePayload = curveResponse.data || {};
      const curves = Array.isArray(curvePayload?.curves) ? curvePayload.curves : [];
      const selectedCurve = curves[0];
      if (!selectedCurve || !Array.isArray(selectedCurve.points) || !selectedCurve.points.length) {
        throw new Error('No MDHD vehicle stock curve is available for the selected utility.');
      }

      const rawEvForecast = simulationYears.reduce((acc, year) => {
        const vehicles = interpolateForecastValue(selectedCurve.points, year);
        acc[year] = Number.isFinite(Number(vehicles)) ? Number(vehicles) : 0;
        return acc;
      }, {});
      const evForecast = simulationYears.reduce((acc, year) => {
        acc[year] = Math.max(0, Math.round(Number(rawEvForecast[year] ?? 0)));
        return acc;
      }, {});
      const hdScenarioLabel = 'Class 7-8 HD';
      const generatedCaseName = buildMdhdCaseName(
        utilityName,
        hdScenarioLabel,
        mdhdConfig?.managedCharging?.v2gMode
      );

      const requestData = {
        simulation_engine: 'rust_pointer',
        mode: normalizeManagedChargingMode(mdhdConfig?.managedCharging?.v2gMode),
        depot_charging_power: Number(mdhdConfig?.managedCharging?.depotChargingPower ?? 50),
        enroute_charging_power: Number(mdhdConfig?.managedCharging?.enrouteChargingPower ?? 350),
        target_ev_count: Number(evForecast[simulationYears[simulationYears.length - 1]]) || 1000,
        soc_max: Number(chargingBehaviorConfig?.socMax ?? 0.95),
        soc_min: Number(chargingBehaviorConfig?.socMin ?? 0.10),
        soc_thres: Number(chargingBehaviorConfig?.socThreshold ?? 0.30),
        soc_initial: Number(chargingBehaviorConfig?.socInitial ?? 0.95),
        batt_cap: [100, 200, 300, 400, 500, 600],
        is_depot: true,
        hexagon_has_charging: true,
        utility_filter: [utilityName],
        ev_forecast: evForecast,
        ev_forecast_raw: rawEvForecast,
        output_mode: 'hex_forecast_only',
        persist_result: true,
        include_debug_info: isLocal,
        case_name: generatedCaseName,
        forecast_scenario: hdScenarioLabel,
      };

      if (!mountedRef.current) return;
      setAnalysisPhase('Running MDHD simulation...');
      saveRunStateForUser({
        isRunning: true,
        startedAt,
        requestId,
        phase: 'Running MDHD simulation...',
      });
      const simulationResponse = await axios.post(
        API_ENDPOINTS.stopChainSimulation,
        requestData,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Token ${token}`,
          },
          signal: controller.signal,
        }
      );

      if (!mountedRef.current) return;
      setAnalysisPhase('Finalizing saved case...');
      saveRunStateForUser({
        isRunning: true,
        startedAt,
        requestId,
        phase: 'Finalizing saved case...',
      });
      const savedCaseId = simulationResponse.data?.saved_case?.id;
      if (!savedCaseId) {
        throw new Error('Analysis completed but no saved case was returned.');
      }

      navigate(`/utility/scenarios/cases/${savedCaseId}`);
    } catch (err) {
      if (!mountedRef.current) return;
      if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError' || err?.name === 'AbortError') {
        setAnalysisError('Analysis run canceled.');
      } else {
        setAnalysisError(err.response?.data?.error || err.message || 'Failed to start MDHD analysis.');
      }
    } finally {
      saveRunStateForUser(null);
      if (!mountedRef.current) return;
      setAnalysisLoading(false);
      setAnalysisPhase(null);
      setAnalysisStartedAt(null);
      setAnalysisElapsedSeconds(0);
      setAnalysisAbortController(null);
      setAnalysisRequestId(null);
    }
  }, [analysisLoading, navigate]);

  const cancelAnalysis = useCallback(() => {
    if (!analysisAbortController) return;
    setAnalysisPhase('Canceling request...');
    saveRunStateForUser({
      isRunning: true,
      startedAt: analysisStartedAt || Date.now(),
      requestId: analysisRequestId,
      phase: 'Canceling request...',
    });
    analysisAbortController.abort();
  }, [analysisAbortController, analysisRequestId, analysisStartedAt]);

  const value = useMemo(() => ({
    analysisLoading,
    analysisError,
    analysisPhase,
    analysisElapsedSeconds,
    analysisRequestId,
    startAnalysis,
    cancelAnalysis,
    clearAnalysisError,
    canCancel: Boolean(analysisAbortController),
  }), [
    analysisLoading,
    analysisError,
    analysisPhase,
    analysisElapsedSeconds,
    analysisRequestId,
    startAnalysis,
    cancelAnalysis,
    clearAnalysisError,
    analysisAbortController,
  ]);

  return (
    <UtilityMdhdAnalysisContext.Provider value={value}>
      {children}
      <LongRunningTaskIndicator
        visible={analysisLoading}
        title="Running MDHD analysis..."
        phase={analysisPhase}
        elapsedSeconds={analysisElapsedSeconds}
        onCancel={analysisAbortController ? cancelAnalysis : undefined}
        requestId={analysisRequestId}
      />
    </UtilityMdhdAnalysisContext.Provider>
  );
}

export function useUtilityMdhdAnalysis() {
  const context = useContext(UtilityMdhdAnalysisContext);
  if (!context) {
    throw new Error('useUtilityMdhdAnalysis must be used within UtilityMdhdAnalysisProvider');
  }
  return context;
}
